import queryString from 'query-string'
import { isEmpty, isNil } from 'lodash'

import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { sparseLlmOverride } from '@/views/VoiceAgents/llmHelpers'
import { languageErrorDetail, languagePreviewKey } from '@/views/VoiceAgents/languageHelpers'

import { REQUEST_STATUS } from './voiceAgents.constants'
import {
  setLoading,
  setError,
  setAiAgentUsers,
  setAgentDetail,
  setAgentConfigMissing,
  setAgentNotFound,
  setNode,
  setVariables,
  setVersions,
  setVariableVersions,
  setToolVersions,
  setYaml,
  setChangeRequests,
  upsertChangeRequest,
  setTranscriptChangeRequests,
  upsertTranscriptChangeRequest,
  setVoices,
  setSavedVoice,
  setLlmCatalog,
  setLlmSettings,
  setLlmVersions,
  setLanguageCatalog,
  setLanguageSettings,
  setLanguageVersions,
  setLanguagePreview,
  setCallDataDeletion,
  clearVersionCaches,
} from './voiceAgents.redux'

// =============================================================================
// Async thunks for the voice-agents data layer.
//
// Each thunk: dispatch loading on, call fetchingAPI, dispatch result/error,
// finally dispatch loading off.
//
// Org-scoping: fetchingAPI auto-injects the caller's `organizationid` header
// from Redux state. Admin org-1 users switch the viewing org via the
// `requested_organization_id` query param (the BE's `authorize_for_org`
// resolves it with an org-1 super-admin bypass) — never by overriding the
// header, which header-auth would reject with a 403 for a cross-org request.
// =============================================================================

const baseUrl = () => `${apiService.voiceAgents}/prompts`

// The call-data-deletion routes sit at the service root, one level above the
// Agent Studio prompt endpoints.
const root = () => apiService.voiceAgents

// Repeated toast / error-fallback messages across the parallel prompt /
// variable / tool thunks — hoisted so each literal lives in one place.
const MSG_LOAD_AGENT_FAILED = 'Failed to load agent'
const MSG_LOAD_VERSIONS_FAILED = 'Failed to load versions'
const MSG_REVERT_FAILED = 'Failed to revert'
const MSG_RESET_FAILED = 'Failed to reset'

// `fetchingAPI` rejects 400/409 responses with a raw object (often
// `{detail: ...}`), so `error.message` can be undefined. Normalize so callers
// always have a string to show inline — validation/drift errors are otherwise
// invisible outside the toast.
const apiErrorMessage = (error, fallback) =>
  error?.message || error?.detail || (typeof error === 'string' ? error : fallback)

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

// Fetch the AI-agent users for an org. Passing `orgId` scopes the request to
// a different org via the `requested_organization_id` query param so a Balto
// admin (org 1) can switch the viewing org from the dropdown. The header stays
// the caller's own org, which is what header-auth validates against.
//
// orgId is optional: when undefined/null the request goes out with no query
// param and the BE defaults to the caller's own org (non-admin path).
export const fetchAiAgentUsers = (orgId) => async (dispatch) => {
  dispatch(setLoading({ aiAgentUsers: true }))
  dispatch(setError({ aiAgentUsers: null }))
  try {
    const params = queryString.stringify({ requested_organization_id: orgId || undefined })
    const url = params ? `${baseUrl()}/ai-agent-users?${params}` : `${baseUrl()}/ai-agent-users`
    const data = await fetchingAPI(url, 'GET', dispatch)
    dispatch(
      setAiAgentUsers({
        orgId: data?.orgId ?? orgId ?? null,
        items: Array.isArray(data?.items) ? data.items : [],
      })
    )
  } catch (error) {
    dispatch(setError({ aiAgentUsers: error.message || 'Failed to load AI agent users' }))
    toast.error('Failed to load AI agent users', { cause: error })
  } finally {
    dispatch(setLoading({ aiAgentUsers: false }))
  }
}

// A 404 means the agent doesn't exist, or the caller's org doesn't own it — the
// BE's ownership guard answers 404 for both, by design, so slugs can't be
// enumerated. Either way it's an expected-empty navigation (stale or
// hand-edited URL), so it drives the agentNotFound empty state rather than a
// toast, per "404 is not a toast".
export const fetchAgentConfig = (agentId) => async (dispatch) => {
  if (!agentId) {
    return
  }
  dispatch(setLoading({ agentDetail: true }))
  dispatch(setError({ agentDetail: null }))
  dispatch(setAgentNotFound(false))
  try {
    const params = queryString.stringify({ agent_id: agentId })
    const detail = await fetchingAPI(`${baseUrl()}/config?${params}`, 'GET', dispatch)
    dispatch(setAgentDetail({ agentId, detail }))
  } catch (error) {
    if (error?.status === 404) {
      dispatch(setAgentNotFound(true))
      return
    }
    dispatch(setError({ agentDetail: error.message || MSG_LOAD_AGENT_FAILED }))
    toast.error(MSG_LOAD_AGENT_FAILED, { cause: error })
  } finally {
    dispatch(setLoading({ agentDetail: false }))
  }
}

// Fetch an agent's config by the user id of its AI-agent user. The BE
// resolves user_id → VoiceAgent → AgentPromptConfig and returns the same
// payload shape as `/config/{agent_id}`. We dispatch setAgentDetail with
// the resolved agent_id so the existing detail-page selectors keep working.
// Returns the resolved agent_id (or null on failure) so the caller can
// drive setSelectedAgent.
export const fetchAgentConfigByUserId = (userId) => async (dispatch) => {
  if (!userId) {
    return null
  }
  dispatch(setLoading({ agentDetail: true }))
  dispatch(setError({ agentDetail: null }))
  dispatch(setAgentConfigMissing(false))
  dispatch(setAgentNotFound(false))
  try {
    const params = queryString.stringify({ user_id: userId })
    const detail = await fetchingAPI(`${baseUrl()}/config/by-user?${params}`, 'GET', dispatch)
    const resolvedAgentId = detail?.id
    if (!resolvedAgentId) {
      throw new Error('Agent config response missing id')
    }
    dispatch(setAgentDetail({ agentId: resolvedAgentId, detail }))
    return resolvedAgentId
  } catch (error) {
    // This route answers 404 for two different things, told apart by the BE's
    // `kind` discriminator (never by the `detail` string):
    //   agent_not_found   — no such user, or its owner is soft-deleted, i.e. the
    //                       agent is retired. A dead link: send it to the app's
    //                       404 page rather than rendering agent chrome around
    //                       nothing.
    //   config_not_seeded — the caller's own agent, just not externalized yet.
    //                       An expected empty state, not an error (per "404 is
    //                       not a toast").
    // An older BE that sends no `kind` falls through to the empty state, which is
    // the pre-existing behaviour.
    if (error?.status === 404) {
      if (error?.kind === 'agent_not_found') {
        dispatch(setAgentNotFound(true))
      } else {
        dispatch(setAgentConfigMissing(true))
      }
      return null
    }
    // 403 = the resolved agent belongs to another org (cross-tenant). Same
    // expected-empty navigation as the slug route's 404, so it gets the same
    // empty state instead of a blank editor for someone else's agent. (This
    // route can't collapse the two into one status the way /config does: it
    // still needs 404 to mean "your agent, not externalized yet".)
    if (error?.status === 403) {
      dispatch(setAgentNotFound(true))
      return null
    }
    dispatch(setError({ agentDetail: error.message || MSG_LOAD_AGENT_FAILED }))
    toast.error(MSG_LOAD_AGENT_FAILED, { cause: error })
    return null
  } finally {
    dispatch(setLoading({ agentDetail: false }))
  }
}

export const fetchNode = (agentId, nodeId) => async (dispatch) => {
  if (!agentId || !nodeId) {
    return
  }
  try {
    const params = queryString.stringify({ agent_id: agentId, node_id: nodeId })
    const node = await fetchingAPI(`${baseUrl()}/nodes?${params}`, 'GET', dispatch)
    dispatch(setNode({ agentId, nodeId, node }))
  } catch (error) {
    toast.error('Failed to refresh node', { cause: error })
  }
}

export const fetchVariables = (agentId) => async (dispatch) => {
  if (!agentId) {
    return
  }
  try {
    const params = queryString.stringify({ agent_id: agentId })
    const data = await fetchingAPI(`${baseUrl()}/variables?${params}`, 'GET', dispatch)
    dispatch(setVariables({ agentId, variables: data?.variables || [] }))
  } catch (error) {
    toast.error('Failed to load variables', { cause: error })
  }
}

export const fetchYaml = (agentId) => async (dispatch, getState) => {
  if (!agentId) {
    return
  }
  dispatch(setLoading({ yaml: true }))
  dispatch(setError({ yaml: null }))
  try {
    // BE returns Content-Type: text/yaml, so we bypass fetchingAPI (which
    // pipes everything through handleResponse → response.json()) and use
    // raw fetch + response.text() to keep this scoped to voice agents.
    const { username, organizationid, token } = getState().currentUser
    const params = queryString.stringify({ agent_id: agentId })
    const response = await fetch(`${baseUrl()}/export/yaml?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'auth-type': 'web',
        username,
        organizationid,
        token,
      },
    })
    if (!response.ok) {
      throw new Error(`Failed to load YAML (${response.status})`)
    }
    const yaml = await response.text()
    dispatch(setYaml({ agentId, yaml }))
  } catch (error) {
    dispatch(setError({ yaml: error.message || 'Failed to load YAML' }))
    toast.error('Failed to load YAML', { cause: error })
  } finally {
    dispatch(setLoading({ yaml: false }))
  }
}

export const fetchPromptVersions = (agentId, nodeId) => async (dispatch) => {
  if (!agentId || !nodeId) {
    return
  }
  dispatch(setLoading({ versions: true }))
  dispatch(setError({ versions: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, node_id: nodeId })
    const versions = await fetchingAPI(`${baseUrl()}/nodes/versions?${params}`, 'GET', dispatch)
    dispatch(setVersions({ nodeId, versions: Array.isArray(versions) ? versions : [] }))
  } catch (error) {
    dispatch(setError({ versions: error.message || MSG_LOAD_VERSIONS_FAILED }))
    toast.error('Failed to load version history', { cause: error })
  } finally {
    dispatch(setLoading({ versions: false }))
  }
}

// -----------------------------------------------------------------------------
// Change requests ("comments")
// -----------------------------------------------------------------------------

// One agent-wide fetch; the drawer comments list, the per-tab open-count dot,
// and the Change Requests page are all pure filters over this list. The BE
// caps limit at 500; pagination beyond that is out of scope for now.
// The server reports the author's remaining edit window as a duration, not a
// deadline (the stored timestamps carry no timezone). Turn it into a deadline on
// the browser's own clock the moment the response lands, and keep that on the
// row: a card that remounts — switching the Open/Resolved filter, reopening the
// drawer — would otherwise re-arm a whole fresh window from the cached duration
// and offer an Edit the server has already stopped accepting.
const withEditDeadline = (row) => {
  if (isNil(row) || !row.canEdit) {
    return row
  }
  return { ...row, editExpiresAt: Date.now() + (row.editSecondsRemaining || 0) * 1000 }
}

const withEditDeadlines = (rows) => (Array.isArray(rows) ? rows.map(withEditDeadline) : [])

export const fetchChangeRequests = (agentId) => async (dispatch) => {
  if (!agentId) {
    return
  }
  dispatch(setLoading({ changeRequests: true }))
  dispatch(setError({ changeRequests: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, limit: 500 })
    const data = await fetchingAPI(`${baseUrl()}/requests?${params}`, 'GET', dispatch)
    dispatch(setChangeRequests({ agentId, items: withEditDeadlines(data?.items) }))
  } catch (error) {
    dispatch(setError({ changeRequests: error.message || 'Failed to load change requests' }))
    toast.error('Failed to load change requests', { cause: error })
  } finally {
    dispatch(setLoading({ changeRequests: false }))
  }
}

// Build the POST body for the active comment variant. `general` (existing) is
// a node/agent-level note; `inline` anchors a highlighted span of prompt text
// (always to the baseline — the FE has no prompt-override id, and the BE does
// not validate inline offsets); `transcript` anchors an assistant turn of a
// call, optionally a span within it. The transcript highlight triple is
// all-or-none: omit it entirely for a whole-turn comment.
const buildRequestBody = ({
  requestType,
  agentId,
  message,
  targetType,
  targetId,
  highlightStart,
  highlightEnd,
  highlightSnippet,
  callId,
  turnStartMs,
}) => {
  if (requestType === 'inline') {
    return {
      request_type: 'inline',
      agent_id: agentId,
      message,
      target_type: targetType,
      target_id: targetId,
      anchor_baseline: true,
      highlight_start: highlightStart,
      highlight_end: highlightEnd,
      highlight_snippet: highlightSnippet,
    }
  }
  if (requestType === 'transcript') {
    const body = {
      request_type: 'transcript',
      // BE ignores agent_id for transcript (it derives it from the call) but
      // still requires the field to be present.
      agent_id: agentId || '_',
      message,
      call_id: callId,
      turn_start_ms: turnStartMs,
    }
    if (!isEmpty(highlightSnippet)) {
      body.highlight_start = highlightStart
      body.highlight_end = highlightEnd
      body.highlight_snippet = highlightSnippet
    }
    return body
  }
  return {
    request_type: 'general',
    agent_id: agentId,
    message,
    target_type: targetType,
    target_id: targetId,
  }
}

export const createChangeRequest =
  ({
    requestType = 'general',
    agentId,
    message,
    targetType,
    targetId,
    highlightStart,
    highlightEnd,
    highlightSnippet,
    callId,
    turnStartMs,
  }) =>
  async (dispatch) => {
    const isTranscript = requestType === 'transcript'
    if (!message || (isTranscript ? !callId : !agentId)) {
      return null
    }
    dispatch(setLoading({ createChangeRequest: true }))
    dispatch(setError({ createChangeRequest: null }))
    try {
      const result = await fetchingAPI(
        `${baseUrl()}/requests`,
        'POST',
        dispatch,
        JSON.stringify(
          buildRequestBody({
            requestType,
            agentId,
            message,
            targetType,
            targetId,
            highlightStart,
            highlightEnd,
            highlightSnippet,
            callId,
            turnStartMs,
          })
        )
      )
      // Transcript comments live in their own call-keyed slice; everything else
      // (general, inline) rides the agent-keyed list via its target.
      if (isTranscript) {
        dispatch(
          upsertTranscriptChangeRequest(
            withEditDeadline({ ...result, callId: result.call?.id ?? callId })
          )
        )
      } else {
        dispatch(
          upsertChangeRequest(withEditDeadline({ ...result, agentId: result.agentId ?? agentId }))
        )
      }
      toast.success('Comment added')
      return result
    } catch (error) {
      dispatch(setError({ createChangeRequest: error.message || 'Failed to add comment' }))
      toast.error(error.message || 'Failed to add comment')
      return null
    } finally {
      dispatch(setLoading({ createChangeRequest: false }))
    }
  }

// Status moves on one comment (resolve / reopen). Both scopes PATCH the same
// row — a comment has one status everywhere — and differ only in which slice
// the updated row lands in: agent-keyed for the Studio lists, call-keyed for
// the Call Explorer's Transcript Comments tab. Passing a callId selects the
// latter.
const patchChangeRequestStatus =
  ({ requestId, body, callId, successMessage, failureMessage }) =>
  async (dispatch, getState) => {
    if (!requestId) {
      return null
    }
    dispatch(setLoading({ resolveChangeRequest: true }))
    dispatch(setError({ resolveChangeRequest: null }))
    // The comment belongs to the currently loaded list's agent. Capture it
    // before the await so the UPSERT staleness guard can reject a response that
    // lands after an agent switch even if the PATCH omits agentId. Only the
    // agent-keyed path needs it — a call-scoped dispatch must not depend on the
    // agent list having been loaded at all.
    const agentId = isNil(callId) ? getState().voiceAgents.changeRequests.agentId : null
    try {
      const result = await fetchingAPI(
        `${baseUrl()}/requests/${requestId}`,
        'PATCH',
        dispatch,
        JSON.stringify(body)
      )
      if (isNil(callId)) {
        dispatch(
          upsertChangeRequest(withEditDeadline({ ...result, agentId: result.agentId ?? agentId }))
        )
      } else {
        dispatch(
          upsertTranscriptChangeRequest(
            withEditDeadline({ ...result, callId: result.call?.id ?? callId })
          )
        )
      }
      toast.success(successMessage)
      return result
    } catch (error) {
      dispatch(setError({ resolveChangeRequest: error.message || failureMessage }))
      toast.error(error.message || failureMessage)
      return null
    } finally {
      dispatch(setLoading({ resolveChangeRequest: false }))
    }
  }

// An empty note is no note: send the bare status rather than a blank
// resolution_note, so nothing renders a "Resolution note:" with nothing in it.
const resolveBody = (resolutionNote) => {
  const note = (resolutionNote || '').trim()
  return isEmpty(note)
    ? { status: REQUEST_STATUS.RESOLVED }
    : { status: REQUEST_STATUS.RESOLVED, resolution_note: note }
}

export const resolveChangeRequest = (requestId, resolutionNote) =>
  patchChangeRequestStatus({
    requestId,
    body: resolveBody(resolutionNote),
    successMessage: 'Comment resolved',
    failureMessage: 'Failed to resolve comment',
  })

// Reopen drops the resolution stamp server-side (resolver, date and note), so
// the row that comes back is a clean Open comment again.
export const reopenChangeRequest = (requestId) =>
  patchChangeRequestStatus({
    requestId,
    body: { status: REQUEST_STATUS.OPEN },
    successMessage: 'Comment reopened',
    failureMessage: 'Failed to reopen comment',
  })

// Call-scoped sibling of fetchChangeRequests. Transcript comments are listed by
// call (the Transcript Comments tab on the Call Explorer), not by agent, so they get
// their own fetch + call-keyed slice rather than the agentId-keyed one.
export const fetchTranscriptChangeRequests = (callId) => async (dispatch) => {
  if (!callId) {
    return
  }
  dispatch(setLoading({ transcriptChangeRequests: true }))
  dispatch(setError({ transcriptChangeRequests: null }))
  try {
    const params = queryString.stringify({
      call_id: callId,
      request_type: 'transcript',
      limit: 500,
    })
    const data = await fetchingAPI(`${baseUrl()}/requests?${params}`, 'GET', dispatch)
    dispatch(
      setTranscriptChangeRequests({
        callId,
        items: withEditDeadlines(data?.items),
      })
    )
  } catch (error) {
    dispatch(setError({ transcriptChangeRequests: error.message || 'Failed to load comments' }))
    toast.error('Failed to load comments', { cause: error })
  } finally {
    dispatch(setLoading({ transcriptChangeRequests: false }))
  }
}

// Rewrite a comment's own text (AVR-877). The server allows this only to the
// author and only for a couple of minutes after posting, so the card offers it
// on the same terms — a 403 here means the window closed while the box was open.
// Same two-slice routing as the status moves: callId picks the call-keyed slice.
export const editChangeRequestMessage =
  (requestId, message, callId) => async (dispatch, getState) => {
    const text = (message || '').trim()
    if (!requestId || isEmpty(text)) {
      return null
    }
    dispatch(setLoading({ editChangeRequest: true }))
    dispatch(setError({ editChangeRequest: null }))
    // Staleness guard, read only on the agent-keyed path — see the status moves.
    const agentId = isNil(callId) ? getState().voiceAgents.changeRequests.agentId : null
    try {
      const result = await fetchingAPI(
        `${baseUrl()}/requests/${requestId}`,
        'PATCH',
        dispatch,
        JSON.stringify({ message: text })
      )
      if (isNil(callId)) {
        dispatch(
          upsertChangeRequest(withEditDeadline({ ...result, agentId: result.agentId ?? agentId }))
        )
      } else {
        dispatch(
          upsertTranscriptChangeRequest(
            withEditDeadline({ ...result, callId: result.call?.id ?? callId })
          )
        )
      }
      toast.success('Comment updated')
      return result
    } catch (error) {
      dispatch(setError({ editChangeRequest: error.message || 'Failed to update comment' }))
      toast.error(error.message || 'Failed to update comment')
      return null
    } finally {
      dispatch(setLoading({ editChangeRequest: false }))
    }
  }

// Place an Unassigned transcript comment on a node by hand (AVR-878). The
// server matches most spoken lines back to their prompt on creation; this is
// the fallback for the ones it cannot, and for the ones it got wrong.
export const linkChangeRequestToNode =
  (requestId, nodeId, callId) => async (dispatch, getState) => {
    if (!requestId || !nodeId) {
      return null
    }
    dispatch(setLoading({ linkChangeRequest: true }))
    dispatch(setError({ linkChangeRequest: null }))
    // Same staleness guard as the status moves, read only on the agent-keyed
    // path — a link made from the call page must not require the Studio list.
    const agentId = isNil(callId) ? getState().voiceAgents.changeRequests.agentId : null
    try {
      const result = await fetchingAPI(
        `${baseUrl()}/requests/${requestId}`,
        'PATCH',
        dispatch,
        JSON.stringify({ target_type: 'prompt', target_id: nodeId })
      )
      // A transcript comment lives in both slices' worlds: it is listed by call
      // on the Call Explorer and by agent in Studio. Update whichever one the
      // caller is looking at — the other refetches on its next mount.
      if (isNil(callId)) {
        dispatch(
          upsertChangeRequest(withEditDeadline({ ...result, agentId: result.agentId ?? agentId }))
        )
      } else {
        dispatch(
          upsertTranscriptChangeRequest(
            withEditDeadline({ ...result, callId: result.call?.id ?? callId })
          )
        )
      }
      toast.success('Comment linked to node')
      return result
    } catch (error) {
      dispatch(setError({ linkChangeRequest: error.message || 'Failed to link comment' }))
      toast.error(error.message || 'Failed to link comment')
      return null
    } finally {
      dispatch(setLoading({ linkChangeRequest: false }))
    }
  }

// -----------------------------------------------------------------------------
// Writes (POC: prompt only)
// -----------------------------------------------------------------------------

export const savePrompt = (agentId, nodeId, content, summary) => async (dispatch) => {
  if (!agentId || !nodeId) {
    return null
  }
  dispatch(setLoading({ savePrompt: true }))
  dispatch(setError({ savePrompt: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, node_id: nodeId })
    const result = await fetchingAPI(
      `${baseUrl()}/nodes?${params}`,
      'PATCH',
      dispatch,
      JSON.stringify({ content, summary })
    )
    // Refresh node + versions so the drawer sees the new state.
    await Promise.all([
      dispatch(fetchNode(agentId, nodeId)),
      dispatch(fetchPromptVersions(agentId, nodeId)),
    ])
    toast.success('Prompt saved')
    return result
  } catch (error) {
    // A misplaced `${language_instructions}` anchor comes back as a 400 whose
    // string `detail` is written for the person who made the edit, and a raw
    // `.message` read would drop it for the generic fallback.
    const message = apiErrorMessage(error, 'Failed to save prompt')
    dispatch(setError({ savePrompt: message }))
    toast.error(message, { cause: error })
    return null
  } finally {
    dispatch(setLoading({ savePrompt: false }))
  }
}

export const revertPrompt = (agentId, nodeId, version, displayName) => async (dispatch) => {
  if (!agentId || !nodeId || !version) {
    return null
  }
  dispatch(setLoading({ revertPrompt: true }))
  dispatch(setError({ revertPrompt: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, node_id: nodeId })
    const result = await fetchingAPI(
      `${baseUrl()}/nodes/revert?${params}`,
      'POST',
      dispatch,
      JSON.stringify({ version })
    )
    await Promise.all([
      dispatch(fetchNode(agentId, nodeId)),
      dispatch(fetchPromptVersions(agentId, nodeId)),
    ])
    toast.success(`Reverted prompt for "${displayName || nodeId}" to v${version}`)
    return result
  } catch (error) {
    // Any version predating the anchor check can trip it on revert, and nothing
    // says which until the call returns — so the BE's reason must survive.
    const message = apiErrorMessage(error, MSG_REVERT_FAILED)
    dispatch(setError({ revertPrompt: message }))
    toast.error(message, { cause: error })
    return null
  } finally {
    dispatch(setLoading({ revertPrompt: false }))
  }
}

export const resetPrompt = (agentId, nodeId, displayName) => async (dispatch) => {
  if (!agentId || !nodeId) {
    return null
  }
  dispatch(setLoading({ resetPrompt: true }))
  dispatch(setError({ resetPrompt: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, node_id: nodeId })
    const result = await fetchingAPI(`${baseUrl()}/nodes/override?${params}`, 'DELETE', dispatch)
    await Promise.all([
      dispatch(fetchNode(agentId, nodeId)),
      dispatch(fetchPromptVersions(agentId, nodeId)),
    ])
    toast.success(`Reset prompt for "${displayName || nodeId}" to default`)
    return result
  } catch (error) {
    dispatch(setError({ resetPrompt: error.message || MSG_RESET_FAILED }))
    toast.error(error.message || MSG_RESET_FAILED)
    return null
  } finally {
    dispatch(setLoading({ resetPrompt: false }))
  }
}

// -----------------------------------------------------------------------------
// Variable lifecycle (edit / version history / revert / reset)
// -----------------------------------------------------------------------------
// BE rejects edits to non-editable variables with 422 (see _find_variable in
// prompt_externalization.py). The UI also hides edit affordances on those
// rows, but the rejection is the source of truth.

export const fetchVariableVersions = (agentId, varName) => async (dispatch) => {
  if (!agentId || !varName) {
    return
  }
  dispatch(setLoading({ varVersions: true }))
  dispatch(setError({ varVersions: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, var_name: varName })
    const versions = await fetchingAPI(`${baseUrl()}/variables/versions?${params}`, 'GET', dispatch)
    dispatch(setVariableVersions({ varName, versions: Array.isArray(versions) ? versions : [] }))
  } catch (error) {
    dispatch(setError({ varVersions: error.message || MSG_LOAD_VERSIONS_FAILED }))
    toast.error('Failed to load variable history', { cause: error })
  } finally {
    dispatch(setLoading({ varVersions: false }))
  }
}

export const declareVariable =
  (agentId, varName, { defaultValue, description, type, scope }) =>
  async (dispatch) => {
    if (!agentId || !varName) {
      return null
    }
    dispatch(setLoading({ declareVariable: true }))
    dispatch(setError({ declareVariable: null }))
    try {
      const params = queryString.stringify({ agent_id: agentId, var_name: varName })
      const result = await fetchingAPI(
        `${baseUrl()}/variables?${params}`,
        'POST',
        dispatch,
        JSON.stringify({
          default_value: defaultValue,
          description,
          type,
          scope,
        })
      )
      await dispatch(fetchVariables(agentId))
      toast.success(`Variable "${varName}" added`)
      return result
    } catch (error) {
      dispatch(setError({ declareVariable: error.message || 'Failed to add variable' }))
      toast.error(error.message || 'Failed to add variable')
      return null
    } finally {
      dispatch(setLoading({ declareVariable: false }))
    }
  }

export const saveVariable = (agentId, varName, value, summary) => async (dispatch) => {
  if (!agentId || !varName) {
    return null
  }
  dispatch(setLoading({ saveVariable: true }))
  dispatch(setError({ saveVariable: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, var_name: varName })
    const result = await fetchingAPI(
      `${baseUrl()}/variables?${params}`,
      'PATCH',
      dispatch,
      JSON.stringify({ value, summary })
    )
    await Promise.all([
      dispatch(fetchVariables(agentId)),
      dispatch(fetchVariableVersions(agentId, varName)),
    ])
    toast.success('Variable saved')
    return result
  } catch (error) {
    dispatch(setError({ saveVariable: error.message || 'Failed to save variable' }))
    toast.error(error.message || 'Failed to save variable')
    return null
  } finally {
    dispatch(setLoading({ saveVariable: false }))
  }
}

export const revertVariable = (agentId, varName, version) => async (dispatch) => {
  if (!agentId || !varName || !version) {
    return null
  }
  dispatch(setLoading({ revertVariable: true }))
  dispatch(setError({ revertVariable: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, var_name: varName })
    const result = await fetchingAPI(
      `${baseUrl()}/variables/revert?${params}`,
      'POST',
      dispatch,
      JSON.stringify({ version })
    )
    await Promise.all([
      dispatch(fetchVariables(agentId)),
      dispatch(fetchVariableVersions(agentId, varName)),
    ])
    toast.success(`Reverted variable "${varName}" to v${version}`)
    return result
  } catch (error) {
    dispatch(setError({ revertVariable: error.message || MSG_REVERT_FAILED }))
    toast.error(error.message || MSG_REVERT_FAILED)
    return null
  } finally {
    dispatch(setLoading({ revertVariable: false }))
  }
}

export const resetVariable = (agentId, varName) => async (dispatch) => {
  if (!agentId || !varName) {
    return null
  }
  dispatch(setLoading({ resetVariable: true }))
  dispatch(setError({ resetVariable: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, var_name: varName })
    const result = await fetchingAPI(
      `${baseUrl()}/variables/override?${params}`,
      'DELETE',
      dispatch
    )
    await Promise.all([
      dispatch(fetchVariables(agentId)),
      dispatch(fetchVariableVersions(agentId, varName)),
    ])
    toast.success(`Reset variable "${varName}" to default`)
    return result
  } catch (error) {
    dispatch(setError({ resetVariable: error.message || MSG_RESET_FAILED }))
    toast.error(error.message || MSG_RESET_FAILED)
    return null
  } finally {
    dispatch(setLoading({ resetVariable: false }))
  }
}

// -----------------------------------------------------------------------------
// Tool lifecycle — same shape as variables; the editable field is the tool's
// description.
// -----------------------------------------------------------------------------

export const fetchToolVersions = (agentId, toolName) => async (dispatch) => {
  if (!agentId || !toolName) {
    return
  }
  dispatch(setLoading({ toolVersions: true }))
  dispatch(setError({ toolVersions: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, tool_name: toolName })
    const versions = await fetchingAPI(`${baseUrl()}/tools/versions?${params}`, 'GET', dispatch)
    dispatch(setToolVersions({ toolName, versions: Array.isArray(versions) ? versions : [] }))
  } catch (error) {
    dispatch(setError({ toolVersions: error.message || MSG_LOAD_VERSIONS_FAILED }))
    toast.error('Failed to load tool history', { cause: error })
  } finally {
    dispatch(setLoading({ toolVersions: false }))
  }
}

export const saveTool = (agentId, toolName, description, summary) => async (dispatch) => {
  if (!agentId || !toolName) {
    return null
  }
  dispatch(setLoading({ saveTool: true }))
  dispatch(setError({ saveTool: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, tool_name: toolName })
    const result = await fetchingAPI(
      `${baseUrl()}/tools?${params}`,
      'PATCH',
      dispatch,
      JSON.stringify({ description, summary })
    )
    // Tool descriptions live on the tool node — refresh the whole agent
    // config so every consumer (drawer ToolsTab, info pane, etc.) re-reads
    // the new description in one shot.
    await Promise.all([
      dispatch(fetchAgentConfig(agentId)),
      dispatch(fetchToolVersions(agentId, toolName)),
    ])
    toast.success('Tool saved')
    return result
  } catch (error) {
    dispatch(setError({ saveTool: error.message || 'Failed to save tool' }))
    toast.error(error.message || 'Failed to save tool')
    return null
  } finally {
    dispatch(setLoading({ saveTool: false }))
  }
}

export const revertTool = (agentId, toolName, version) => async (dispatch) => {
  if (!agentId || !toolName || !version) {
    return null
  }
  dispatch(setLoading({ revertTool: true }))
  dispatch(setError({ revertTool: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, tool_name: toolName })
    const result = await fetchingAPI(
      `${baseUrl()}/tools/revert?${params}`,
      'POST',
      dispatch,
      JSON.stringify({ version })
    )
    await Promise.all([
      dispatch(fetchAgentConfig(agentId)),
      dispatch(fetchToolVersions(agentId, toolName)),
    ])
    toast.success(`Reverted tool "${toolName}" to v${version}`)
    return result
  } catch (error) {
    dispatch(setError({ revertTool: error.message || MSG_REVERT_FAILED }))
    toast.error(error.message || MSG_REVERT_FAILED)
    return null
  } finally {
    dispatch(setLoading({ revertTool: false }))
  }
}

export const resetTool = (agentId, toolName) => async (dispatch) => {
  if (!agentId || !toolName) {
    return null
  }
  dispatch(setLoading({ resetTool: true }))
  dispatch(setError({ resetTool: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, tool_name: toolName })
    const result = await fetchingAPI(`${baseUrl()}/tools/override?${params}`, 'DELETE', dispatch)
    await Promise.all([
      dispatch(fetchAgentConfig(agentId)),
      dispatch(fetchToolVersions(agentId, toolName)),
    ])
    toast.success(`Reset tool "${toolName}" to default`)
    return result
  } catch (error) {
    dispatch(setError({ resetTool: error.message || MSG_RESET_FAILED }))
    toast.error(error.message || MSG_RESET_FAILED)
    return null
  } finally {
    dispatch(setLoading({ resetTool: false }))
  }
}

// -----------------------------------------------------------------------------
// LLM settings (AVR-527) — per-sub-agent model / temperature / reasoning
// effort with agent-level defaults. node_id '__agent__' addresses the
// agent-level target; the endpoints otherwise mirror the prompt/tool override
// trio. Save/revert/reset validation errors carry a user-facing `detail`
// written by the BE — surface it verbatim.
// -----------------------------------------------------------------------------

export const fetchLlmCatalog = () => async (dispatch) => {
  dispatch(setLoading({ llmCatalog: true }))
  dispatch(setError({ llmCatalog: null }))
  try {
    const data = await fetchingAPI(`${baseUrl()}/llm/catalog`, 'GET', dispatch)
    dispatch(setLlmCatalog(Array.isArray(data?.models) ? data.models : []))
  } catch (error) {
    dispatch(setError({ llmCatalog: error.message || 'Failed to load model catalog' }))
    toast.error('Failed to load model catalog', { cause: error })
  } finally {
    dispatch(setLoading({ llmCatalog: false }))
  }
}

export const fetchLlmSettings = (agentId, nodeId) => async (dispatch) => {
  if (!agentId || !nodeId) {
    return
  }
  dispatch(setLoading({ llmSettings: true }))
  dispatch(setError({ llmSettings: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, node_id: nodeId })
    const settings = await fetchingAPI(`${baseUrl()}/llm?${params}`, 'GET', dispatch)
    dispatch(setLlmSettings({ agentId, nodeId, settings }))
  } catch (error) {
    dispatch(setError({ llmSettings: error.message || 'Failed to load LLM settings' }))
    toast.error('Failed to load LLM settings', { cause: error })
  } finally {
    dispatch(setLoading({ llmSettings: false }))
  }
}

export const fetchLlmVersions = (agentId, nodeId) => async (dispatch) => {
  if (!agentId || !nodeId) {
    return
  }
  dispatch(setLoading({ llmVersions: true }))
  dispatch(setError({ llmVersions: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, node_id: nodeId })
    const versions = await fetchingAPI(`${baseUrl()}/llm/versions?${params}`, 'GET', dispatch)
    dispatch(setLlmVersions({ agentId, nodeId, versions: Array.isArray(versions) ? versions : [] }))
  } catch (error) {
    dispatch(setError({ llmVersions: error.message || MSG_LOAD_VERSIONS_FAILED }))
    toast.error('Failed to load LLM settings history', { cause: error })
  } finally {
    dispatch(setLoading({ llmVersions: false }))
  }
}

// An LLM write can change effective values beyond its own target — an
// agent-level save cascades to every sub-agent, and the config payload embeds
// effective values per node — so refresh the whole agent config alongside the
// target's settings + history.
const refreshLlmState = (dispatch, agentId, nodeId) =>
  Promise.all([
    dispatch(fetchLlmSettings(agentId, nodeId)),
    dispatch(fetchLlmVersions(agentId, nodeId)),
    dispatch(fetchAgentConfig(agentId)),
  ])

// The body replaces the whole override, and field PRESENCE is meaningful:
// omitted = "inherit", explicit null = "cleared" (suppress inherited values),
// value = set. `sparseLlmOverride` keeps only the present fields, so untouched
// fields are never accidentally cleared.
export const saveLlmSettings = (agentId, nodeId, override) => async (dispatch) => {
  if (!agentId || !nodeId) {
    return null
  }
  dispatch(setLoading({ saveLlm: true }))
  dispatch(setError({ saveLlm: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, node_id: nodeId })
    const result = await fetchingAPI(
      `${baseUrl()}/llm?${params}`,
      'PATCH',
      dispatch,
      JSON.stringify(sparseLlmOverride(override))
    )
    await refreshLlmState(dispatch, agentId, nodeId)
    toast.success('LLM settings saved')
    return result
  } catch (error) {
    const message = apiErrorMessage(error, 'Failed to save LLM settings')
    dispatch(setError({ saveLlm: message }))
    toast.error(message, { cause: error })
    return null
  } finally {
    dispatch(setLoading({ saveLlm: false }))
  }
}

export const revertLlmSettings = (agentId, nodeId, version) => async (dispatch) => {
  if (!agentId || !nodeId || !version) {
    return null
  }
  dispatch(setLoading({ revertLlm: true }))
  dispatch(setError({ revertLlm: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, node_id: nodeId })
    const result = await fetchingAPI(
      `${baseUrl()}/llm/revert?${params}`,
      'POST',
      dispatch,
      JSON.stringify({ version })
    )
    await refreshLlmState(dispatch, agentId, nodeId)
    toast.success(`Reverted LLM settings to v${version}`)
    return result
  } catch (error) {
    const message = apiErrorMessage(error, MSG_REVERT_FAILED)
    dispatch(setError({ revertLlm: message }))
    toast.error(message, { cause: error })
    return null
  } finally {
    dispatch(setLoading({ revertLlm: false }))
  }
}

export const resetLlmSettings = (agentId, nodeId) => async (dispatch) => {
  if (!agentId || !nodeId) {
    return null
  }
  dispatch(setLoading({ resetLlm: true }))
  dispatch(setError({ resetLlm: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId, node_id: nodeId })
    const result = await fetchingAPI(`${baseUrl()}/llm/override?${params}`, 'DELETE', dispatch)
    await refreshLlmState(dispatch, agentId, nodeId)
    toast.success('Reset LLM settings to baseline')
    // Truthy success sentinel even if the BE ever answers 204 (fetchingAPI
    // resolves undefined for empty bodies) — callers close confirms on it.
    return result ?? true
  } catch (error) {
    const message = apiErrorMessage(error, MSG_RESET_FAILED)
    dispatch(setError({ resetLlm: message }))
    toast.error(message, { cause: error })
    return null
  } finally {
    dispatch(setLoading({ resetLlm: false }))
  }
}

// -----------------------------------------------------------------------------
// Language settings (AVR-678): which languages the agent speaks and which it
// starts in. The BE writes the voice provider first and persists only if the
// provider confirms, so a 502 means nothing changed. All routes take the
// Studio slug as `agent_id` (it contains a slash, hence a query param).
// -----------------------------------------------------------------------------
export const fetchLanguageCatalog = () => async (dispatch) => {
  dispatch(setLoading({ languageCatalog: true }))
  dispatch(setError({ languageCatalog: null }))
  try {
    // The reducer normalises both lists; passing the payload through keeps one
    // place responsible for the shape the panel maps over.
    const data = await fetchingAPI(`${baseUrl()}/languages/catalog`, 'GET', dispatch)
    dispatch(setLanguageCatalog(data))
  } catch (error) {
    // 404 = the language routes aren't served yet, so the whole feature is an
    // expected empty state the panel hides — not an actionable error.
    if (error?.status === 404) {
      dispatch(setError({ languageCatalog: { kind: 'unavailable' } }))
      return
    }
    dispatch(setError({ languageCatalog: error.message || 'Failed to load language catalog' }))
    toast.error('Failed to load language catalog', { cause: error })
  } finally {
    dispatch(setLoading({ languageCatalog: false }))
  }
}

export const fetchLanguageSettings = (agentId) => async (dispatch) => {
  if (!agentId) {
    return
  }
  dispatch(setError({ languageSettings: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId })
    const settings = await fetchingAPI(`${baseUrl()}/languages?${params}`, 'GET', dispatch)
    dispatch(setLanguageSettings({ agentId, settings }))
  } catch (error) {
    // 404 = the BE doesn't serve language settings for this agent (or the
    // routes aren't deployed yet): an expected empty state the panel hides,
    // not an actionable error.
    if (error?.status === 404) {
      dispatch(setError({ languageSettings: { kind: 'unavailable' } }))
      return
    }
    dispatch(setError({ languageSettings: { kind: 'retryable' } }))
    toast.error('Failed to load language settings', { cause: error })
  }
}

export const fetchLanguageVersions = (agentId) => async (dispatch) => {
  if (!agentId) {
    return
  }
  try {
    const params = queryString.stringify({ agent_id: agentId })
    const versions = await fetchingAPI(`${baseUrl()}/languages/versions?${params}`, 'GET', dispatch)
    dispatch(setLanguageVersions({ agentId, versions }))
  } catch (error) {
    // As with the catalog, a 404 means the feature isn't served yet: an
    // expected empty history, not a failure worth a toast. Any other failure
    // leaves the history empty and says so once — the panel renders the card
    // from `languageSettings`, so there is no history error state to show.
    if (error?.status !== 404) {
      toast.error('Failed to load language history', { cause: error })
    }
  }
}

// The generated LANGUAGE text for a set that hasn't been saved yet, so the
// rules an operator reads while editing describe the languages they just
// picked. The BE renders it (one template, one source of the prose) and lints
// any custom text in force against the hypothetical set. Runs as the selection
// changes, so a failure never reaches the operator — never a toast.
export const previewLanguageInstructions =
  (agentId, { languages, startLanguage }) =>
  async (dispatch) => {
    if (!agentId || isEmpty(languages)) {
      return
    }
    const key = languagePreviewKey(languages, startLanguage)
    try {
      const params = queryString.stringify({
        agent_id: agentId,
        languages: languages.join(','),
        start_language: startLanguage ?? undefined,
      })
      const data = await fetchingAPI(`${baseUrl()}/languages/preview?${params}`, 'GET', dispatch)
      dispatch(
        setLanguagePreview({
          agentId,
          key,
          languageInstructions: data?.languageInstructions,
        })
      )
    } catch (error) {
      // No error state and no toast: this runs as the operator edits the
      // selection, and the panel already degrades by showing the saved text
      // with the set it describes.
      console.warn('Failed to preview language rules', error)
    }
  }

// A language write changes the rendered `language_instructions` default that
// rides on the config payload, so refresh the agent config alongside the
// settings and history.
const refreshLanguageState = (dispatch, agentId) =>
  Promise.all([
    dispatch(fetchLanguageSettings(agentId)),
    dispatch(fetchLanguageVersions(agentId)),
    dispatch(fetchAgentConfig(agentId)),
  ])

// The four language writes share one shape: loading/error bookkeeping under
// `key`, the request, a refresh of whatever the write can change, one toast.
// Errors are stored as { message, items[] } so the panel can list voice gaps
// and per-agent provider failures. Resolves the response (`true` for an empty
// body) on success and null on failure, like the LLM thunks.
const languageWrite =
  ({ key, request, refresh, successMessage, fallback }) =>
  async (dispatch, getState) => {
    dispatch(setLoading({ [key]: true }))
    // The panel shows these four through one inline slot, so clearing only
    // `key` would let a losing sibling's error paint under the next write's
    // success toast, with nothing left on screen to dismiss it.
    dispatch(
      setError({
        saveLanguages: null,
        resetLanguages: null,
        revertLanguages: null,
      })
    )
    try {
      const result = await request(dispatch)
      await refresh(dispatch)
      toast.success(successMessage)
      return result ?? true
    } catch (error) {
      const detail = languageErrorDetail(
        error,
        fallback,
        getState().voiceAgents.languageCatalog.languages
      )
      dispatch(setError({ [key]: detail }))
      toast.error(detail.message, { cause: error })
      return null
    } finally {
      dispatch(setLoading({ [key]: false }))
    }
  }

// `startLanguage` and `summary` are optional on the BE (omitted start = the
// first entry); the body is camelCase like the other Studio PATCHes. The BE
// sets `extra="forbid"`, so any stale field here is a 422 rather than a silent
// drop — which is what `sttPreset` became on 2026-09-18, when the ASR model
// stopped being choosable and the field was deleted. Do not reintroduce it.
//
// `languageInstructions` is the one field that came back: removed 2026-09-14
// when the block became generated-only, accepted again from 2026-09-17 as
// verbatim wording the agent owns, stored beside the languages. Presence
// carries it — omit to keep what is stored, null to go back to generated — and
// JSON.stringify dropping `undefined` is what implements "omit".
export const saveLanguageSettings =
  (agentId, { languages, startLanguage, languageInstructions, summary }) =>
  async (dispatch, getState) => {
    if (!agentId) {
      return null
    }
    const params = queryString.stringify({ agent_id: agentId })
    return languageWrite({
      key: 'saveLanguages',
      request: (innerDispatch) =>
        fetchingAPI(
          `${baseUrl()}/languages?${params}`,
          'PATCH',
          innerDispatch,
          JSON.stringify({ languages, startLanguage, languageInstructions, summary })
        ),
      refresh: (innerDispatch) => refreshLanguageState(innerDispatch, agentId),
      successMessage: 'Languages saved',
      fallback: 'Failed to save languages',
    })(dispatch, getState)
  }

export const resetLanguageSettings = (agentId) => async (dispatch, getState) => {
  if (!agentId) {
    return null
  }
  const params = queryString.stringify({ agent_id: agentId })
  return languageWrite({
    key: 'resetLanguages',
    request: (innerDispatch) =>
      fetchingAPI(`${baseUrl()}/languages/override?${params}`, 'DELETE', innerDispatch),
    refresh: (innerDispatch) => refreshLanguageState(innerDispatch, agentId),
    successMessage: 'Reset languages to baseline',
    fallback: MSG_RESET_FAILED,
  })(dispatch, getState)
}

// `wordingOnly` restores that version's LANGUAGE wording and leaves today's
// language set alone (BE 423b6098). The BE takes it as `only:
// "languageInstructions"` and 400s on any other value, so the string is a
// literal here rather than something a caller can pass through. Either mode
// writes a new version instead of rewinding, so history is never lost.
export const revertLanguageSettings =
  (agentId, version, { wordingOnly = false } = {}) =>
  async (dispatch, getState) => {
    if (!agentId || !version) {
      return null
    }
    const params = queryString.stringify({ agent_id: agentId })
    return languageWrite({
      key: 'revertLanguages',
      request: (innerDispatch) =>
        fetchingAPI(
          `${baseUrl()}/languages/revert?${params}`,
          'POST',
          innerDispatch,
          JSON.stringify(wordingOnly ? { version, only: 'languageInstructions' } : { version })
        ),
      refresh: (innerDispatch) => refreshLanguageState(innerDispatch, agentId),
      successMessage: wordingOnly
        ? `Restored the language rules from v${version}`
        : `Reverted languages to v${version}`,
      fallback: MSG_REVERT_FAILED,
    })(dispatch, getState)
  }

// `syncLanguageSettings` is deliberately absent (2026-09-18). A save no longer
// PATCHes the provider agent — language settings reach Cartesia per call from
// `pre_call_handler` — so there is no agent-level state to drift from or push
// onto, and `POST /prompts/languages/sync` 404s.

// -----------------------------------------------------------------------------
// Promotion: cross-env overrides export/import (AVR-392)
// -----------------------------------------------------------------------------

// Export: ask the BE for the agent's overrides as a YAML blob, trigger a
// browser download. The BE returns text/yaml so we bypass fetchingAPI (same
// pattern as fetchYaml above).
export const exportOverrides = (agentId) => async (dispatch, getState) => {
  if (!agentId) {
    return null
  }
  dispatch(setLoading({ exportOverrides: true }))
  dispatch(setError({ exportOverrides: null }))
  try {
    const { username, organizationid, token } = getState().currentUser
    const params = queryString.stringify({ agent_id: agentId })
    const response = await fetch(`${baseUrl()}/promotion/export?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'auth-type': 'web',
        username,
        organizationid,
        token,
      },
    })
    if (!response.ok) {
      let detail = `Export failed (${response.status})`
      try {
        const json = await response.clone().json()
        if (json?.detail) detail = json.detail
      } catch (_) {
        // fall through with default detail
      }
      throw new Error(detail)
    }
    const yamlText = await response.text()
    // Trigger a browser download. URL.createObjectURL keeps the data
    // off the location bar (CR/LF in YAML breaks data: URIs).
    const filename = `${agentId.replace(/\//g, '_')}_overrides.yaml`
    const blob = new Blob([yamlText], { type: 'application/x-yaml' })
    const url = URL.createObjectURL(blob)
    try {
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } finally {
      // Defer revoke so the browser has time to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    }
    toast.success(`Exported overrides for "${agentId}"`)
    return yamlText
  } catch (error) {
    dispatch(setError({ exportOverrides: error.message || 'Export failed' }))
    toast.error(error.message || 'Export failed')
    return null
  } finally {
    dispatch(setLoading({ exportOverrides: false }))
  }
}

// Import: dry-run or apply. Always run dry-run first to populate the diff
// preview; the modal calls again with dry_run:false when the operator clicks
// Apply, passing back expected_target_hash from the dry-run response so the
// BE can detect drift between the two calls.
//
// Returns the BE response dict — caller (modal) renders the diff and decides
// whether to call again with dry_run:false.
export const importOverrides =
  (agentId, yamlText, options = {}) =>
  async (dispatch) => {
    if (!agentId || !yamlText) {
      return null
    }
    const { dryRun = true, expectedTargetHash = null } = options
    const loadingKey = dryRun ? 'importOverridesDryRun' : 'importOverridesApply'
    dispatch(setLoading({ [loadingKey]: true }))
    dispatch(setError({ importOverrides: null }))
    try {
      const params = queryString.stringify({ agent_id: agentId })
      const result = await fetchingAPI(
        `${baseUrl()}/promotion/import?${params}`,
        'POST',
        dispatch,
        JSON.stringify({
          yaml: yamlText,
          dry_run: dryRun,
          expected_target_hash: expectedTargetHash,
        })
      )
      if (!dryRun && result?.applied) {
        toast.success(`Imported overrides for "${agentId}"`)
        // Refresh local state so the editors show the new values.
        await dispatch(fetchAgentConfig(agentId))
      }
      return result
    } catch (error) {
      const message = apiErrorMessage(error, 'Import failed')
      dispatch(setError({ importOverrides: message }))
      toast.error(message, { cause: error })
      return { ok: false, error: message }
    } finally {
      dispatch(setLoading({ [loadingKey]: false }))
    }
  }

// -----------------------------------------------------------------------------
// Reseed config (org-1 only, AVR-559)
// -----------------------------------------------------------------------------

// Replace the agent's entire stored prompt config with pasted JSON
// (PUT /config) — the API counterpart of the reseed SQL scripts. The BE
// requires the JSON's own agent_id to match this agent (a paste meant for
// another agent 422s) and bumps config_version; per-node/variable/tool
// overrides are untouched. Returns { ok, config_version? } or
// { ok:false, error } for inline rendering in the modal.
export const reseedAgentConfig =
  (agentId, config, options = {}) =>
  async (dispatch) => {
    if (!agentId || !config) {
      return null
    }
    // newGeneration stores the paste as the *next* generation, leaving the
    // previous generation's row and overrides intact but dormant; `activate`
    // then decides whether the agent switches to it now or it is staged for
    // later. Default false: the everyday reseed refreshes the active generation
    // in place, where existing edits must keep applying.
    //
    // bump_version is always true — config_version is an audit counter within a
    // generation, and writing a config without moving it would make the trail
    // lie about what a call ran.
    const { newGeneration = false, activate = true } = options
    dispatch(setLoading({ reseedConfig: true }))
    dispatch(setError({ reseedConfig: null }))
    try {
      const params = queryString.stringify({ agent_id: agentId })
      const result = await fetchingAPI(
        `${baseUrl()}/config?${params}`,
        'PUT',
        dispatch,
        JSON.stringify({
          config,
          bump_version: true,
          new_generation: newGeneration,
          activate,
        })
      )
      toast.success(`Config reseeded for "${agentId}"`)
      // A new generation changes which overrides exist, so the cached version
      // lists are stale in exactly the same way as after an activate.
      if (newGeneration) {
        dispatch(clearVersionCaches())
      }
      // Refresh so the canvas/editors render the new config immediately.
      await dispatch(fetchAgentConfig(agentId))
      return { ok: true, ...result }
    } catch (error) {
      const message = apiErrorMessage(error, 'Reseed failed')
      dispatch(setError({ reseedConfig: message }))
      return { ok: false, error: message }
    } finally {
      dispatch(setLoading({ reseedConfig: false }))
    }
  }

// Every stored generation of this agent, newest first, each with the number of
// overrides that belong to it — the rollback menu. Errors stay silent and
// return null; the caller renders an empty list rather than blocking.
export const fetchConfigGenerations = (agentId) => async (dispatch) => {
  if (!agentId) {
    return null
  }
  dispatch(setLoading({ configGenerations: true }))
  try {
    const params = queryString.stringify({ agent_id: agentId })
    return await fetchingAPI(`${baseUrl()}/config/generations?${params}`, 'GET', dispatch)
  } catch (error) {
    return null
  } finally {
    dispatch(setLoading({ configGenerations: false }))
  }
}

// Make a stored generation the live one. Nothing is copied and no override is
// written — the agent's pointer moves, so the target generation's edits come
// back exactly as they were left and the one being stepped away from keeps its
// own. Refreshes the config so the canvas reflects the switch immediately.
export const activateConfigGeneration = (agentId, generation) => async (dispatch) => {
  if (!agentId || !generation) {
    return null
  }
  dispatch(setLoading({ activateGeneration: true }))
  dispatch(setError({ activateGeneration: null }))
  try {
    const params = queryString.stringify({ agent_id: agentId })
    const result = await fetchingAPI(
      `${baseUrl()}/config/activate?${params}`,
      'POST',
      dispatch,
      JSON.stringify({ generation })
    )
    toast.success(`Generation ${generation} is now active for "${agentId}"`)
    // The per-target version caches describe the generation we just left.
    dispatch(clearVersionCaches())
    await dispatch(fetchAgentConfig(agentId))
    return { ok: true, ...result }
  } catch (error) {
    const message = apiErrorMessage(error, 'Could not activate that generation')
    dispatch(setError({ activateGeneration: message }))
    return { ok: false, error: message }
  } finally {
    dispatch(setLoading({ activateGeneration: false }))
  }
}

// -----------------------------------------------------------------------------
// Demo call (org-1 only)
// -----------------------------------------------------------------------------

// Place a live demo call: the BE resolves the agent's Cartesia credentials and
// the agent dials `phoneNumber` (AVR-428). `metadata` is forwarded verbatim in
// Cartesia's outbound-call body; the agent reads it from call_request.metadata.
// Returns the BE result or null.
export const startDemoCall =
  (agentId, phoneNumber, metadata = {}) =>
  async (dispatch) => {
    if (!agentId || !phoneNumber) {
      return null
    }
    try {
      const result = await fetchingAPI(
        `${baseUrl()}/demo-call`,
        'POST',
        dispatch,
        JSON.stringify({ agent_id: agentId, phone_number: phoneNumber, metadata })
      )
      toast.success(`Calling ${phoneNumber} — answer to talk to the agent`)
      // Truthy success sentinel even if the BE ever answers 204 (fetchingAPI
      // resolves undefined for empty bodies) — callers close the modal on it.
      return result ?? true
    } catch (error) {
      // Same normalization as importOverrides: some statuses reject with a raw
      // `{detail: ...}` object, so `error.message` can be undefined.
      const message =
        error?.message ||
        error?.detail ||
        (typeof error === 'string' ? error : 'Failed to place demo call')
      toast.error(message, { cause: error })
      return null
    }
  }

// -----------------------------------------------------------------------------
// Web call (org-1 only)
// -----------------------------------------------------------------------------

// Mint a single-use ticket for a browser web call (AVR-532). A WebSocket can't
// carry an Authorization header, so this authenticated POST does the auth and
// the socket redeems the returned ticket. The ticket is short-lived, so open
// the socket immediately. `metadata` is forwarded verbatim in the Cartesia
// stream's start event; the agent reads it from call_request.metadata.
// Returns {ticket, expiresIn, streamPath} or null.
export const createWebCallSession =
  (agentId, { metadata = {}, callerNumber = null } = {}) =>
  async (dispatch) => {
    if (!agentId) {
      return null
    }
    try {
      return await fetchingAPI(
        `${baseUrl()}/web-call/session`,
        'POST',
        dispatch,
        // `caller_number` is omitted rather than sent null when unset: the BE
        // validates it as E.164 when present, and an explicit null would make
        // "not given" indistinguishable from "given and empty".
        JSON.stringify({
          agent_id: agentId,
          metadata,
          ...(callerNumber ? { caller_number: callerNumber } : {}),
        })
      )
    } catch (error) {
      // Same normalization as startDemoCall: some statuses reject with a raw
      // `{detail: ...}` object, so `error.message` can be undefined.
      const message =
        error?.message ||
        error?.detail ||
        (typeof error === 'string' ? error : 'Failed to start web call')
      toast.error(message, { cause: error })
      return null
    }
  }

// The web-call socket lives on the voice-agents service, so it shares that
// origin — swap the scheme rather than assuming the page's own host.
export const webCallSocketUrl = (streamPath, ticket) => {
  const base = String(apiService.voiceAgents || '').replace(/^http/, 'ws')
  return `${base}${streamPath}?ticket=${encodeURIComponent(ticket)}`
}

// -----------------------------------------------------------------------------
// Voices (Overview-tab picker)
//
// All three endpoints proxy the TTS provider through the BE (white-labeled —
// the provider never surfaces in the UI). The preview/say endpoints return raw
// audio bytes, so they bypass fetchingAPI (which JSON-parses everything) the
// same way fetchYaml does, and hand back object URLs for <Audio> playback —
// the caller owns revoking them.
// -----------------------------------------------------------------------------

// 500 = no voice provider configured for this tenant (non-retryable),
// 404 = unknown agent (expected-empty, not an error toast),
// anything else (502 upstream, network) is worth a retry.
const voicesErrorFromStatus = (status, detail) => {
  if (status === 500) {
    return { kind: 'notConfigured', detail }
  }
  if (status === 404) {
    return { kind: 'unavailable', detail }
  }
  return { kind: 'retryable', detail }
}

const audioAuthHeaders = (getState) => {
  const { username, organizationid, token } = getState().currentUser
  return { 'auth-type': 'web', username, organizationid, token }
}

// Load the full voice catalog for an agent, following the `starting_after`
// cursor until `hasMore` is false (the catalog is a few hundred voices, so
// search/filtering stay client-side). `orgId` threads the admin org scope
// (`requested_organization_id`) like the other Agent Studio thunks.
export const fetchVoices = (agentId, orgId) => async (dispatch) => {
  if (!agentId) {
    return
  }
  dispatch(setLoading({ voices: true }))
  dispatch(setError({ voices: null }))
  try {
    const items = []
    let cursor
    let selectedVoiceId = null
    do {
      const params = queryString.stringify({
        agent_id: agentId,
        limit: 100,
        starting_after: cursor,
        requested_organization_id: orgId || undefined,
      })
      // eslint-disable-next-line no-await-in-loop
      const page = await fetchingAPI(`${baseUrl()}/voices?${params}`, 'GET', dispatch)
      items.push(...(Array.isArray(page?.voices) ? page.voices : []))
      // The persisted selection rides on every page (same value) — it seeds
      // the picker's preselected voice.
      selectedVoiceId = page?.selectedVoiceId ?? null
      cursor = page?.hasMore ? page.nextPage : undefined
    } while (cursor)
    dispatch(setVoices({ agentId, items, selectedVoiceId }))
  } catch (error) {
    dispatch(setError({ voices: voicesErrorFromStatus(error?.status, error?.message) }))
  } finally {
    dispatch(setLoading({ voices: false }))
  }
}

// Fetch a voice's canned preview audio (free). `previewUrl` is the relative
// path from the catalog response (it already carries `agent_id`). Returns an
// object URL, or null on failure.
export const fetchVoicePreview = (previewUrl, orgId) => async (dispatch, getState) => {
  if (!previewUrl) {
    return null
  }
  try {
    const orgParams = queryString.stringify({ requested_organization_id: orgId || undefined })
    const url = `${apiService.voiceAgents}${previewUrl}${orgParams ? `&${orgParams}` : ''}`
    const response = await fetch(url, { headers: audioAuthHeaders(getState) })
    if (!response.ok) {
      throw new Error(`Preview failed (${response.status})`)
    }
    return URL.createObjectURL(await response.blob())
  } catch (error) {
    toast.error('Failed to play the voice preview', { cause: error })
    return null
  }
}

// Generate custom-phrase audio with a voice ("say this"). Each call spends
// real TTS credits, so callers must only trigger it on an explicit click.
// Returns an object URL, or null on failure (error lands in error.sayVoice
// for inline display — 422s carry the BE's validation message).
export const sayVoicePhrase =
  ({ agentId, voiceId, transcript, orgId }) =>
  async (dispatch, getState) => {
    if (!agentId || !voiceId || !transcript) {
      return null
    }
    dispatch(setLoading({ sayVoice: true }))
    dispatch(setError({ sayVoice: null }))
    try {
      const params = queryString.stringify({
        agent_id: agentId,
        requested_organization_id: orgId || undefined,
      })
      const response = await fetch(`${baseUrl()}/voices/say?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...audioAuthHeaders(getState) },
        body: JSON.stringify({ voice_id: voiceId, transcript }),
      })
      if (!response.ok) {
        let detail = `Playback failed (${response.status})`
        try {
          const json = await response.clone().json()
          if (typeof json?.detail === 'string') {
            detail = json.detail
          }
        } catch (parseError) {
          // fall through with the default detail
        }
        dispatch(
          setError({
            sayVoice: { kind: response.status === 422 ? 'validation' : 'retryable', detail },
          })
        )
        return null
      }
      return URL.createObjectURL(await response.blob())
    } catch (error) {
      dispatch(setError({ sayVoice: { kind: 'retryable', detail: 'Playback failed' } }))
      return null
    } finally {
      dispatch(setLoading({ sayVoice: false }))
    }
  }

// Persist the voice selection. The local commit happens only after the BE
// confirms — the saved voice then preselects on future loads via the GET's
// `selectedVoiceId`. Note: the saved voice doesn't apply to live calls until
// the BE's pre_call_handler wiring lands (AVR-472).
export const saveVoiceSelection = (agentId, voiceId, orgId) => async (dispatch) => {
  if (!agentId || !voiceId) {
    return
  }
  dispatch(setLoading({ saveVoice: true }))
  try {
    const params = queryString.stringify({
      agent_id: agentId,
      requested_organization_id: orgId || undefined,
    })
    const result = await fetchingAPI(
      `${baseUrl()}/voices?${params}`,
      'PATCH',
      dispatch,
      JSON.stringify({ voice_id: voiceId })
    )
    dispatch(setSavedVoice(result?.voiceId ?? voiceId))
    toast.success('Voice saved')
  } catch (error) {
    // The voice save 400s with an object detail when the voice lacks a
    // configured language; string details and plain errors still normalise.
    toast.error(languageErrorDetail(error, 'Failed to save voice').message)
  } finally {
    dispatch(setLoading({ saveVoice: false }))
  }
}

// -----------------------------------------------------------------------------
// Call-data deletion at the voice provider (admin org only)
// -----------------------------------------------------------------------------
//
// GET/PUT /voice-agents/{voice_agent_id}/call-data-deletion. `voiceAgentId` is
// the voice_agents master-row id (the provisioning record's `id`) — not the
// provider's agent id and not the prompt-config agent id. Both routes are
// admin-org only and take no `requested_organization_id`: an admin addresses
// any agent by id directly. Responses are snake_case, unlike /prompts/*.
const CALL_DATA_DELETION_URL = (voiceAgentId) =>
  `${root()}/voice-agents/${voiceAgentId}/call-data-deletion`

// 403 (not a Balto admin) and 404 (no such agent) both mean "there is nothing
// to show here", which the panel renders as nothing at all rather than an error.
// The agent id rides along so the panel can ignore a failure that belongs to a
// previously-viewed agent.
const deletionErrorFromStatus = (status, detail, voiceAgentId) =>
  status === 403 || status === 404
    ? { kind: 'unavailable', detail, voiceAgentId }
    : { kind: 'retryable', detail, voiceAgentId }

export const fetchCallDataDeletion = (voiceAgentId) => async (dispatch) => {
  if (isNil(voiceAgentId)) {
    return
  }
  dispatch(setLoading({ callDataDeletion: true }))
  dispatch(setError({ callDataDeletion: null }))
  try {
    const data = await fetchingAPI(CALL_DATA_DELETION_URL(voiceAgentId), 'GET', dispatch)
    dispatch(setCallDataDeletion({ voiceAgentId, enabled: Boolean(data?.enabled) }))
  } catch (error) {
    dispatch(
      setError({
        callDataDeletion: deletionErrorFromStatus(
          error?.status,
          apiErrorMessage(error, null),
          voiceAgentId
        ),
      })
    )
  } finally {
    dispatch(setLoading({ callDataDeletion: false }))
  }
}

// Resolves truthy only once the new value is stored, so the caller closes its
// confirmation on the same condition — and the toggle keeps rendering the last
// stored value on failure instead of appearing to have worked.
export const saveCallDataDeletion = (voiceAgentId, enabled) => async (dispatch) => {
  if (isNil(voiceAgentId)) {
    return null
  }
  dispatch(setLoading({ saveCallDataDeletion: true }))
  dispatch(setError({ saveCallDataDeletion: null }))
  try {
    const data = await fetchingAPI(
      CALL_DATA_DELETION_URL(voiceAgentId),
      'PUT',
      dispatch,
      JSON.stringify({ enabled })
    )
    dispatch(setCallDataDeletion({ voiceAgentId, enabled: Boolean(data?.enabled) }))
    toast.success(enabled ? 'Call-data deletion enabled' : 'Call-data deletion disabled')
    return true
  } catch (error) {
    const message = apiErrorMessage(error, 'Failed to save call-data deletion')
    dispatch(setError({ saveCallDataDeletion: message }))
    toast.error(message, { cause: error })
    return null
  } finally {
    dispatch(setLoading({ saveCallDataDeletion: false }))
  }
}
